<template>
    <div>
        <div class="flex items-center justify-between">
            <div>
                <p class="mb-0">
                    <a :href="`/subject-areas/listing/${contentListing.subjectAreaId}`" class="underline">Back to {{ contentListing.subjectAreaName }}</a>
                </p>
                <h2 class="mb-5 mt-0">
                    Quiz/Exam Submissions for {{contentListing.name}}
                </h2>
            </div>
        </div>

        <!-- Tab Navigation -->
        <div class="flex border-b border-gray-200 mb-5">
            <button 
                class="px-4 py-2 font-medium text-sm"
                :class="activeTab === 'quizzes' ? 'text-black border-b-2 border-black' : 'text-gray-800 hover:text-gray-800'"
                @click="activeTab = 'quizzes'"
            >
                Quizzes
            </button>
            <button 
                class="px-4 py-2 font-medium text-sm ml-8"
                :class="activeTab === 'exams' ? 'text-black border-b-2 border-black': 'text-gray-800 hover:text-gray-800'"
                @click="activeTab = 'exams'"
            >
                Exams
            </button>
        </div>

        <!-- Quiz Tab Content -->
        <div v-if="activeTab === 'quizzes'">

        <div v-if="quizDefinitions.length > 0">
            <h3>Retrieve quiz excel reports</h3>
            <p>Download quiz reports using the link(s) below.</p>
            <div v-for="quizDefinition in quizDefinitions" :key="quizDefinition.id">
                <a @click="getQuizReportFile(quizDefinition.id)" href="#" class="block mb-2"><u>{{quizDefinition.name}}</u></a>
            </div>
        </div>

        <h3>View submission data</h3>
        <div class="flex items-center">
            <d-dropdown trap-focus>
                <d-btn slot="activator" flat class="mr-3">
                    <d-icon icon="filter" class="pt-1" />
                </d-btn>
                <d-dropdown-item :focusable="false" custom paddingless>
                    <div style="width:250px">
                        <d-field label="Search by User or Quiz Name">
                            <d-input v-model="filter" />
                        </d-field>
                    </div>
                </d-dropdown-item>
            </d-dropdown>
        </div>
        <d-table 
            class="bg-white shadow mt-10 mb-5" 
            :data="quizzes" 
            :columns="tableColumns"
            :loading="loading" 
            backend-search 
            backend-sorting 
            :default-sort="[pagination.sortField, pagination.sortOrder]" 
            striped 
            detail-key="id" 
            detailed 
            default-sort-direction="desc" 
            @sort="onSort">
            <template #default="{ row }">
                <td>{{ formatDate(row.dateTime) }}</td>
                <td>{{ row.userFullName }}</td>
                <td>{{ row.quizName }}</td>
                <td>
                    <a href="javascript:;" class="underline" @click.stop="launchFeedbackModal(row)">
                        {{ row.feedback ? 'View/Edit Feedback' : 'Provide Feedback' }}
                    </a>
                </td>
            </template>
            <template #detail="{ row }">
                <quiz-answers-report-table :quiz="row" />
            </template>
        </d-table>

        <div class="flex justify-end">            
            <d-pagination :total-items="total" :per-page="pagination.pageSize" v-model:current="pagination.page" />
        </div>
        </div>

        <!-- Exam Tab Content -->
        <div v-if="activeTab === 'exams'">
            <div v-if="examDefinitions.length > 0">
                <h3>Retrieve exam excel reports</h3>
                <p>Download exam reports using the link(s) below.</p>
                <div v-for="examDefinition in examDefinitions" :key="examDefinition.id">
                    <a @click="getExamReportFile(examDefinition.id)" href="#" class="block mb-2"><u>{{examDefinition.name}}</u></a>
                </div>
            </div>

            <h3>Exam Submissions</h3>
            <div class="flex items-center mb-4">
                <div class="mr-4">
                    <d-field label="Search by User Name, Email, or Exam Name">
                        <d-input v-model="examFilter" placeholder="Type to search..." />
                    </d-field>
                </div>
                <d-dropdown trap-focus>
                    <d-btn slot="activator" flat class="mr-3">
                        <d-icon icon="filter" class="pt-1" />
                    </d-btn>
                    <d-dropdown-item :focusable="false" custom paddingless>
                        <div style="width:250px">
                            <d-field label="Search by User Name, Email, or Exam Name">
                                <d-input v-model="examFilter" />
                            </d-field>
                        </div>
                    </d-dropdown-item>
                </d-dropdown>
            </div>
            
            <d-table 
                class="bg-white shadow mt-5 mb-5" 
                :data="examReports" 
                :columns="examTableColumns"
                :loading="examLoading" 
                backend-search 
                backend-sorting 
                :default-sort="[examPagination.sortField, examPagination.sortOrder]"
                striped 
                default-sort-direction="asc"
                @sort="onExamSort"
            >
                <template #default="{ row }">
                    <td>{{ row.examName }}</td>
                    <td>{{ row.userFirstName }} {{ row.userLastName }}</td>
                    <td>{{ row.userEmail }}</td>
                    <td>{{ row.isStarted ? `${row.questionsCorrect}/${row.questionsTotal}` : 'N/A' }}</td>
                    <td>{{ row.isStarted ? `${Math.round(row.scorePercentage)}%` : 'N/A' }}</td>
                    <td>
                        <span v-if="!row.isStarted" class="text-gray-600">
                            Not Started
                        </span>
                        <span v-else-if="!isExamCompleted(row)" class="text-blue-600">
                            In Progress
                        </span>
                        <span v-else :class="row.passed ? 'text-green-600' : 'text-red-600'">
                            {{ row.passed ? 'Passed' : 'Failed' }}
                        </span>
                    </td>
                    <td>{{ row.isStarted && row.startedAt && row.startedAt !== '0001-01-01T00:00:00+00:00' && row.startedAt !== '0001-01-01T00:00:00.000Z' ? formatDate(row.startedAt) : 'N/A' }}</td>
                    <td>{{ row.isStarted && isExamCompleted(row) && row.completedAt && row.completedAt !== '0001-01-01T00:00:00+00:00' && row.completedAt !== '0001-01-01T00:00:00.000Z' ? formatDate(row.completedAt) : 'N/A' }}</td>
                    <td>
                        <a v-if="row.isStarted && isExamCompleted(row)" href="javascript:;" class="underline" @click.stop="launchExamFeedbackModal(row)">
                            {{ row.instructorFeedback ? 'View/Edit Feedback' : 'Provide Feedback' }}
                        </a>
                        <span v-else class="text-gray-400">-</span>
                    </td>
                    <td>
                        <div class="flex gap-2">
                            <button 
                                v-if="row.isStarted"
                                class="btn btn-sm btn-outline-primary"
                                @click="viewExamDetails(row)"
                            >
                                View Details
                            </button>
                            <button 
                                v-if="row.isStarted"
                                class="btn btn-sm btn-outline-danger"
                                @click="resetExamProgress(row.examId, row.userId)"
                                :disabled="resettingExam"
                            >
                                Reset
                            </button>
                            <span v-if="!row.isStarted" class="text-gray-400">-</span>
                        </div>
                    </td>
                </template>
            </d-table>

            <div class="flex justify-end" v-if="examTotal > 0">            
                <d-pagination :total-items="examTotal" :per-page="examPagination.pageSize" v-model:current="examPagination.page" />
            </div>
            
            <!-- Debug info (remove after testing) -->
            <div class="text-sm text-gray-500 mt-2" v-if="examReports.length > 0">
                Showing {{ examReports.length }} of {{ examTotal }} total items (Page {{ examPagination.page }})
            </div>
        </div>
    </div>

    <!-- Exam Feedback Modal -->
    <d-modal
        :active="showExamFeedbackModal"
        :width="850"
        @close="closeExamFeedbackModal"
        :can-cancel="['escape', 'x', 'outside']"
    >
        <ExamFeedbackModal
            v-if="selectedExam"
            :exam="selectedExam"
            @close="closeExamFeedbackModal"
        />
    </d-modal>

    <!-- Exam Details Modal -->
    <d-modal
        :active="showExamDetailsModal"
        :width="1200"
        @close="closeExamDetailsModal"
        :can-cancel="['escape', 'x', 'outside']"
    >
        <div v-if="selectedExamForDetails" class="modal-card" style="width: auto">
            <header class="modal-card-head">
                <p class="modal-card-title">
                    Exam Details: {{ selectedExamForDetails.examName }}
                </p>
            </header>
            <section class="modal-card-body">
                <div class="mb-4">
                    <h4 class="text-lg font-semibold mb-2">Student Information</h4>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <strong>Name:</strong> {{ selectedExamForDetails.userFirstName }} {{ selectedExamForDetails.userLastName }}
                        </div>
                        <div>
                            <strong>Email:</strong> {{ selectedExamForDetails.userEmail }}
                        </div>
                        <div>
                            <strong>Score:</strong> {{ selectedExamForDetails.questionsCorrect }}/{{ selectedExamForDetails.questionsTotal }} 
                            ({{ Math.round(selectedExamForDetails.scorePercentage) }}%)
                        </div>
                        <div>
                            <strong>Status:</strong> 
                            <span v-if="!selectedExamForDetails.isStarted" class="text-gray-600">Not Started</span>
                            <span v-else-if="!isExamCompleted(selectedExamForDetails)" class="text-blue-600">In Progress</span>
                            <span v-else :class="selectedExamForDetails.passed ? 'text-green-600' : 'text-red-600'">
                                {{ selectedExamForDetails.passed ? 'Passed' : 'Failed' }}
                            </span>
                        </div>
                        <div>
                            <strong>Started:</strong> 
                            {{ selectedExamForDetails.isStarted && selectedExamForDetails.startedAt && selectedExamForDetails.startedAt !== '0001-01-01T00:00:00+00:00' && selectedExamForDetails.startedAt !== '0001-01-01T00:00:00.000Z' ? formatDate(selectedExamForDetails.startedAt) : 'N/A' }}
                        </div>
                        <div>
                            <strong>Completed:</strong> 
                            {{ selectedExamForDetails.isStarted && isExamCompleted(selectedExamForDetails) && selectedExamForDetails.completedAt && selectedExamForDetails.completedAt !== '0001-01-01T00:00:00+00:00' && selectedExamForDetails.completedAt !== '0001-01-01T00:00:00.000Z' ? formatDate(selectedExamForDetails.completedAt) : 'N/A' }}
                        </div>
                    </div>
                </div>
                
                <div>
                    <h4 class="text-lg font-semibold mb-2">Detailed Answers</h4>
                    <exam-answers-report-table v-if="selectedExamForDetails.isStarted" :exam="selectedExamForDetails" />
                    <div v-else class="p-4 text-gray-600 bg-gray-50 rounded">
                        No answers available - exam not started.
                    </div>
                </div>
            </section>
            <footer class="modal-card-foot justify-end">
                <d-btn flat @click="closeExamDetailsModal">
                    Close
                </d-btn>
            </footer>
        </div>
    </d-modal>
</template>

<script>
    import axios from 'axios';
    import debounce from 'lodash/debounce';
    import QuizFeedbackModal from './components/QuizFeedbackModal.vue';
    import ExamFeedbackModal from './components/ExamFeedbackModal.vue';
    import ExamAnswersReportTable from './components/ExamAnswersReportTable.vue';
    import { formatDate } from '@utils/formatters'

    export default {
        parent: true,

        components: {
            ExamFeedbackModal,
            ExamAnswersReportTable,
        },

        props: {
            contentListing: {
                type: Object,
                default: () => ({}),
            },
        },

        data() {
            return {
                activeTab: 'quizzes',
                quizDefinitions: [],
                quizzes: [],
                loading: false,
                examReports: [],
                examDefinitions: [],
                examLoading: false,
                resettingExam: false,
                filter: '',
                examFilter: '',
                total: 0,
                examTotal: 0,
                pagination: {
                    page: 1,
                    pageSize: 20,
                    sortField: 'lastModifiedDate',
                    sortOrder: 'desc',
                },
                examPagination: {
                    page: 1,
                    pageSize: 20,
                    sortField: 'username',
                    sortOrder: 'asc',
                },
                tableColumns: [
                    { field: 'dateTime', label: 'Last Accessed', sortable: true },
                    { field: 'userFullName', label: 'User Name', sortable: false },
                    { field: 'quizName', label: 'Quiz Name', sortable: false },
                    { field: 'feedback', label: 'Feedback', sortable: false }
                ],
                examTableColumns: [
                    { field: 'examName', label: 'Exam Name', sortable: true },
                    { field: 'userName', label: 'User Name', sortable: true },
                    { field: 'userEmail', label: 'Email', sortable: true },
                    { field: 'score', label: 'Score', sortable: false },
                    { field: 'percentage', label: 'Percentage', sortable: true },
                    { field: 'status', label: 'Status', sortable: false },
                    { field: 'startedAt', label: 'Started', sortable: true },
                    { field: 'completedAt', label: 'Completed', sortable: false },
                    { field: 'feedback', label: 'Feedback', sortable: false },
                    { field: 'actions', label: 'Actions', sortable: false }
                ],
                showExamFeedbackModal: false,
                selectedExam: null,
                showExamDetailsModal: false,
                selectedExamForDetails: null
            };
        },

        watch: {
            pagination: {
                handler() {
                    this.getPaged();
                },
                deep: true,
            },
            examPagination: {
                handler() {
                    this.getExamReports();
                },
                deep: true,
            },
            filter() {
                this.getDebouncedPaged();
            },
            examFilter() {
                this.getDebouncedExamReports();
            },
            activeTab(newTab) {
                if (newTab === 'exams') {
                    // Reset pagination when switching to exams tab
                    this.examPagination.page = 1;
                    this.examFilter = '';
                    this.getExamDefinitions();
                    this.getExamReports();
                } else if (newTab === 'quizzes') {
                    // Reset pagination when switching to quizzes tab
                    this.pagination.page = 1;
                    this.filter = '';
                }
            }
        },

        mounted() {
            this.getQuizDefinitions();
            this.getPaged();
            
            // If starting on exams tab, load exam data
            if (this.activeTab === 'exams') {
                this.getExamDefinitions();
                this.getExamReports();
            }
        },

        methods: {
            async getPaged() {
                const {
                    page, sortField, sortOrder, pageSize,
                } = this.pagination;
                const params = [`filter=${this.filter}`, `sortDir=${sortField} ${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`].join('&');
                this.loading = true;
                const { data } = await axios.get(`/api/subject-areas/content-listings/${this.contentListing.id}/quiz-reports?${params}`);
                this.quizzes = data.resultItems;
                this.total = data.totalRecords;
                this.loading = false;
            },
            async getQuizDefinitions() {
                const { data } = await axios.get(`/api/subject-areas/content-listings/quiz-reports/quizzes-available/${this.contentListing.id}`);
                this.quizDefinitions = data;
            },

            getQuizReportFile(id) {
                window.location.href = `/api/subject-areas/content-listings/quiz-reports/excel/${id}`
            },

            launchFeedbackModal(quiz) {
                this.$modal.open({
                    parent: this,
                    component: QuizFeedbackModal,
                    width: 850,
                    hasModalCard: true,
                    props: {
                        quiz: quiz,
                    },
                    events: {
                        saveDraggable: async () => {
                            const files = await this.saveDraggable();
                            const updatedFile = files.find((m) => m.id === this.file.id);
                            return updatedFile;
                        },
                        getPaged: async () => this.getPaged(),
                    },
                });
            },

            launchExamFeedbackModal(exam) {
                this.selectedExam = exam;
                this.showExamFeedbackModal = true;
            },

            closeExamFeedbackModal() {
                this.showExamFeedbackModal = false;
                this.selectedExam = null;
                // Refresh the exam reports after modal closes
                this.getExamReports();
            },

            viewExamDetails(exam) {
                this.selectedExamForDetails = exam;
                this.showExamDetailsModal = true;
            },

            closeExamDetailsModal() {
                this.showExamDetailsModal = false;
                this.selectedExamForDetails = null;
            },

            // eslint-disable-next-line func-names
            getDebouncedPaged: debounce(function () {
                // Reset to first page when searching
                this.pagination.page = 1;
                this.getPaged();
            }, 500),

            // eslint-disable-next-line func-names
            getDebouncedExamReports: debounce(function () {
                // Reset to first page when searching
                this.examPagination.page = 1;
                this.getExamReports();
            }, 500),

            onSort(field, order) {
                this.pagination.sortField = field;
                this.pagination.sortOrder = order;
            },

            onExamSort(field, order) {
                this.examPagination.sortField = field;
                this.examPagination.sortOrder = order;
            },

            async getExamDefinitions() {
                try {
                    const { data } = await axios.get(`/api/subject-areas/content-listings/exam-reports/exams-available/${this.contentListing.id}`);
                    this.examDefinitions = data;
                } catch (error) {
                    console.error('Error loading exam definitions:', error);
                    this.examDefinitions = [];
                }
            },

            getExamReportFile(examId) {
                window.location.href = `/api/exams/${examId}/reports/export`;
            },

            async getExamReports() {
                this.examLoading = true;
                try {
                    const {
                        page, sortField, sortOrder, pageSize,
                    } = this.examPagination;
                    const params = [`filter=${this.examFilter}`, `sortField=${sortField}`, `sortDirection=${sortOrder}`, `currentPage=${page}`, `pageSize=${pageSize}`].join('&');
                    const { data } = await axios.get(`/api/subject-areas/content-listings/${this.contentListing.id}/exam-reports/paged?${params}`);
                     
                    // Add unique ID for each exam report to support detail functionality
                    this.examReports = data.resultItems ? data.resultItems.map((report, index) => ({
                        ...report,
                        id: `${report.examId}_${report.userId}_${index}`
                    })) : [];
                    
                    this.examTotal = data.totalRecords || data.total || 0;
                } catch (error) {
                    console.error('Error loading exam reports:', error);
                    this.examReports = [];
                    this.examTotal = 0;
                } finally {
                    this.examLoading = false;
                }
            },

            async resetExamProgress(examId, userId) {
                if (!confirm('Are you sure you want to reset this exam progress? This action cannot be undone.')) {
                    return;
                }

                this.resettingExam = true;
                try {
                    await axios.post(`/api/subject-areas/content-listings/exam-reports/reset/${examId}/${userId}`);
                    this.$toast.success('Exam progress has been reset successfully.');
                    this.getExamReports(); // Refresh the exam reports
                } catch (error) {
                    console.error('Error resetting exam progress:', error);
                    this.$toast.error('Failed to reset exam progress.');
                } finally {
                    this.resettingExam = false;
                }
            },

            isExamCompleted(examReport) {
                // An exam is completed when the total questions answered equals the total questions
                // questionsCorrect + questionsIncorrect should equal questionsTotal
                if (!examReport.isStarted || !examReport.questionsTotal) {
                    return false;
                }
                
                const questionsAnswered = (examReport.questionsCorrect || 0) + (examReport.questionsIncorrect || 0);
                return questionsAnswered >= examReport.questionsTotal;
            },

            formatDate
        }
    };
</script>